import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Prompt, useHistory } from 'react-router-dom'
import { makeStyles, Snackbar } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import AdminPage from '../../../../admin/shared/AdminPage'
import SydButton from '../../../../../commonDesign/Button'
import { useCreateEvalRun, useGetAiSkills, useGetAiModels } from '../../../../../../api/denaliAssistant'
import { decipherError } from '../../../../../../utils/decipherError'
import { buildJudgeSpec } from '../shared/judgeSpec'
import { SAMPLE_COUNT_MAX, SAMPLE_COUNT_MIN } from '../shared/defaults'
import EvalRunFormProvider, { useEvalRunForm } from './EvalRunFormContext'
import RunSetupPanel from './RunSetupPanel'
import JudgePanel from './JudgePanel'
import AgentSidePanel from './AgentSidePanel'

const useStyles = makeStyles(() => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    overflow: 'hidden'
  },
  headerBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 'var(--gap-md)',
    padding: 'var(--padding-sm) var(--padding-md)',
    margin: 'var(--margin-md) var(--margin-lg) 0',
    borderRadius: 'var(--border-radius-lg)',
    border: '1px solid var(--color-neutral-300)',
    backgroundColor: 'var(--color-neutral-100)',
    flexShrink: 0
  },
  headerField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  headerFieldName: {
    flex: 1,
    minWidth: '200px'
  },
  headerFieldSamples: {
    width: '100px'
  },
  fieldLabel: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingLeft: '2px'
  },
  fieldDescription: {
    fontSize: '10px',
    fontWeight: 400,
    color: 'var(--color-neutral-400)',
    textTransform: 'none',
    letterSpacing: 0,
    marginLeft: '4px'
  },
  fieldInput: {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: 'var(--color-primary-400)',
      boxShadow: '0 0 0 2px var(--color-primary-100)'
    },
    '&::placeholder': {
      color: 'var(--color-neutral-400)'
    }
  },
  numberInput: {
    fontFamily: 'var(--font-mono, monospace)',
    textAlign: 'right'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-sm)',
    paddingBottom: '2px',
    marginLeft: 'auto'
  },
  body: {
    display: 'flex',
    flex: 1,
    gap: 'var(--gap-lg)',
    padding: 'var(--padding-md) var(--padding-lg) 0',
    minHeight: 0,
    overflow: 'hidden'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '3px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white'
  },
  leftColumn: {
    width: '300px',
    flexShrink: 0
  },
  middleColumn: {
    flex: 1,
    minWidth: '420px'
  },
  rightColumn: {
    width: '380px',
    flexShrink: 0,
    backgroundColor: 'var(--color-ai-050)',
    borderColor: 'var(--color-ai-200, rgba(169,88,255,0.25))'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-lg)',
    padding: '5px var(--padding-lg)',
    flexShrink: 0,
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    borderTop: '1px solid var(--color-neutral-200)'
  },
  statusBarRight: {
    marginLeft: 'auto'
  }
}))

function StatusBar ({ judgeSpec }) {
  const classes = useStyles()
  const form = useEvalRunForm()
  const skillBit = form.skillId
    ? `${form.skillId}${form.skillTag ? ` :${form.skillTag}` : ''}`
    : null

  return (
    <div className={classes.statusBar}>
      <span>{form.sampleCount} sample{form.sampleCount === 1 ? '' : 's'} will be dispatched in parallel</span>
      {skillBit && <span>{'\u00B7'} skill <strong>{form.skillId}</strong>{form.skillTag ? ` :${form.skillTag}` : ''}</span>}
      {form.focus?.subjectLabel && <span>{'\u00B7'} focus {form.focus.subjectLabel}</span>}
      {form.temperature !== null && <span>{'\u00B7'} temp {form.temperature}</span>}
      {form.judgeModelId && <span>{'\u00B7'} judge {form.judgeModelId}</span>}
      <span className={classes.statusBarRight}>
        {form.validMetricCount} metric{form.validMetricCount === 1 ? '' : 's'} {'\u00B7'} schema {Object.keys(judgeSpec.schema.properties).length} field{Object.keys(judgeSpec.schema.properties).length === 1 ? '' : 's'}
      </span>
    </div>
  )
}

StatusBar.propTypes = {
  judgeSpec: PropTypes.object.isRequired
}

function buildPayload (form, judgeSpec) {
  const focusPayload = form.focus?.subjectIdentifier != null
    ? {
      type: form.focus.type || 'client',
      subjectIdentifier: form.focus.subjectIdentifier,
      subjectLabel: form.focus.subjectLabel || ''
    }
    : null

  return {
    ...(form.name && { name: form.name }),
    prompt: form.prompt,
    rubric: form.rubric,
    judgeSchema: judgeSpec.schema,
    sampleCount: form.sampleCount,
    ...(form.judgeModelId && { judgeModelId: form.judgeModelId }),
    ...(Object.keys(judgeSpec.weights).length > 0 && { scoreWeights: judgeSpec.weights }),
    context: {
      ...(form.skillId && { skillId: form.skillId }),
      ...(form.skillTag && { skillTag: form.skillTag }),
      ...(form.modelOverride && { modelOverride: form.modelOverride }),
      ...(form.temperature !== null && form.temperature !== undefined && { temperatureOverride: form.temperature }),
      ...(focusPayload && { focus: focusPayload })
    }
  }
}

function WorkspaceShell ({ root }) {
  const classes = useStyles()
  const history = useHistory()
  const form = useEvalRunForm()
  const createEvalRun = useCreateEvalRun()

  const [toast, setToast] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const { data: skillsData } = useGetAiSkills({ includeAll: true })
  const { data: modelsData } = useGetAiModels()

  const skillOptions = useMemo(() => {
    const skills = skillsData?.skills || []
    const unique = new Map()
    for (const s of skills) {
      if (!unique.has(s.skillId)) unique.set(s.skillId, s)
    }
    return Array.from(unique.values()).sort((a, b) => a.skillId.localeCompare(b.skillId))
  }, [skillsData])

  const modelOptions = useMemo(() => (
    (modelsData?.models || [])
      .filter(m => m.enabled !== false && !m.hidden)
      .sort((a, b) => (a.displayName || a.modelId).localeCompare(b.displayName || b.modelId))
  ), [modelsData])

  const judgeSpec = useMemo(() => buildJudgeSpec(form.metrics), [form.metrics])

  const workspaceContext = useMemo(() => ({
    name: form.name,
    prompt: form.prompt,
    rubric: form.rubric,
    metrics: form.metrics,
    sampleCount: form.sampleCount,
    skillId: form.skillId,
    skillTag: form.skillTag,
    modelOverride: form.modelOverride,
    judgeModelId: form.judgeModelId,
    temperature: form.temperature,
    focus: form.focus
  }), [form.name, form.prompt, form.rubric, form.metrics, form.sampleCount, form.skillId, form.skillTag, form.modelOverride, form.judgeModelId, form.temperature, form.focus])

  const canSubmit = (
    !!form.prompt.trim() &&
    !!form.rubric.trim() &&
    form.hasValidMetric &&
    form.sampleCount >= SAMPLE_COUNT_MIN &&
    form.sampleCount <= SAMPLE_COUNT_MAX &&
    !submitting
  )

  const handleStart = useCallback(async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const payload = buildPayload(form, judgeSpec)
      const result = await createEvalRun.mutateAsync(payload)
      form.isDirtyRef.current = false
      const batchId = result?.batchId
      if (batchId) {
        history.push(`${root}/ai/evals/${batchId}`)
      } else {
        setToast({ severity: 'success', message: 'Eval run created.' })
      }
    } catch (err) {
      setToast({ severity: 'error', message: decipherError(err) })
    } finally {
      setSubmitting(false)
    }
  }, [canSubmit, form, judgeSpec, createEvalRun, history, root])

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handleStart()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleStart])

  useEffect(() => {
    if (!form.isDirty) return undefined
    const handler = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [form.isDirty])

  return (
    <AdminPage>
      <div className={classes.page}>
        <Prompt
          when={form.isDirty}
          message={() => {
            if (!form.isDirtyRef.current) return true
            return 'You have unsaved changes. Are you sure you want to leave?'
          }}
        />

        <div className={classes.headerBar}>
          <div className={`${classes.headerField} ${classes.headerFieldName}`}>
            <label className={classes.fieldLabel}>
              Name <span className={classes.fieldDescription}>display label for this run</span>
            </label>
            <input
              className={classes.fieldInput}
              value={form.name}
              onChange={(e) => form.setName(e.target.value)}
              placeholder='Untitled eval run'
            />
          </div>

          <div className={`${classes.headerField} ${classes.headerFieldSamples}`}>
            <label className={classes.fieldLabel}>Samples</label>
            <input
              type='number'
              min={SAMPLE_COUNT_MIN}
              max={SAMPLE_COUNT_MAX}
              className={`${classes.fieldInput} ${classes.numberInput}`}
              value={form.sampleCount}
              onChange={(e) => form.setSampleCount(parseInt(e.target.value, 10) || SAMPLE_COUNT_MIN)}
            />
          </div>

          <div className={classes.headerActions}>
            <SydButton
              size='sm'
              variant='secondary'
              onClick={form.resetForm}
              disabled={submitting || !form.isDirty}
            >
              Reset
            </SydButton>
            <SydButton
              size='sm'
              variant='secondary'
              onClick={() => history.push(`${root}/ai/evals`)}
              disabled={submitting}
            >
              Back
            </SydButton>
            <SydButton
              size='sm'
              variant='primary'
              icon='check'
              onClick={handleStart}
              disabled={!canSubmit}
            >
              {submitting ? 'Starting\u2026' : 'Start run'}
            </SydButton>
          </div>
        </div>

        <div className={classes.body}>
          <div className={`${classes.column} ${classes.leftColumn}`}>
            <RunSetupPanel skillOptions={skillOptions} modelOptions={modelOptions} />
          </div>
          <div className={`${classes.column} ${classes.middleColumn}`}>
            <JudgePanel modelOptions={modelOptions} />
          </div>
          <div className={`${classes.column} ${classes.rightColumn}`}>
            <AgentSidePanel workspaceContext={workspaceContext} onApplyProposal={form.applyProposal} />
          </div>
        </div>

        <StatusBar judgeSpec={judgeSpec} />
      </div>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert
            severity={toast.severity}
            variant='filled'
            elevation={6}
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </AdminPage>
  )
}

WorkspaceShell.propTypes = {
  root: PropTypes.string
}

function EvalRunWorkspace ({ root }) {
  return (
    <EvalRunFormProvider>
      <WorkspaceShell root={root} />
    </EvalRunFormProvider>
  )
}

EvalRunWorkspace.propTypes = {
  root: PropTypes.string
}

export default EvalRunWorkspace
